import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { __awaiter } from 'tslib';

const TOP = 'top';
const BOTTOM = 'bottom';
const LEFT = 'left';
const RIGHT = 'right';
const CENTER = 'center';
const HIDDEN = 'hidden';
const ARROW_SPACING = 25;
const CLASS_PREFIX = 'se-fe-position';
const POSITION_CLASSES = 'left right center top bottom undefined'.split(' ').map(dir => `${CLASS_PREFIX}--${dir}`);
// TODO: make these events mobile friendly
const HOVER_EVENTS = ['hover'];
const CLICK_EVENTS = ['click'];
class SeFePositionService {
    constructor() {
        this.toggling = false;
        this.triggers = [];
        this.contents = [];
        this.pull = (arr, values) => {
            return arr.filter((item) => {
                return values.indexOf(item) < 0;
            });
        };
        this.documentElement.addEventListener('click', e => this.hideActiveContent(e));
        this.window().addEventListener('resize', () => this.hideActiveContent());
    }
    addTrigger(instance) {
        const events = this.triggerEvents(instance.event);
        const toggle = () => this.toggleContent(instance);
        const trigger = { instance, toggle };
        events.forEach(e => instance.element.addEventListener(e, toggle));
        this.triggers.push(trigger);
    }
    removeTrigger(instance) {
        const trigger = this.triggers.find(t => t.instance === instance);
        const events = this.triggerEvents(instance.event);
        events.forEach(e => instance.element.removeEventListener(e, trigger.toggle));
        this.triggers = this.pull(this.triggers, [trigger]);
    }
    addContent(content) {
        this.contents.push(content);
    }
    removeContent(content) {
        this.contents = this.pull(this.contents, [content]);
    }
    getScrollParent(el) {
        const win = this.window();
        let parent = el.parentElement;
        while (parent) {
            const style = win.getComputedStyle(parent);
            const overflow = `${style.overflow}${style.overflowY}`;
            const search = /scroll|auto/;
            if (search.exec(overflow))
                return parent;
            parent = parent.parentElement;
        }
    }
    getScroll(el) {
        const parent = this.getScrollParent(el);
        const parentOffset = parent === el.offsetParent;
        return { top: parentOffset ? parent.scrollTop : 0, left: parentOffset ? parent.scrollLeft : 0 };
    }
    triggerEvents(triggerEvent) {
        return triggerEvent === 'hover' ? HOVER_EVENTS : CLICK_EVENTS;
    }
    hideActiveContent(e) {
        if (this.toggling || !this.activeName || this.clickedInside(e))
            return;
        this.contents.forEach(content => {
            content.name === this.activeName && content.hide();
            this.currentParentElement.removeEventListener('scroll', this.updatePos);
        });
        delete this.activeName;
    }
    clickedInside(e) {
        if (!e || !e.target) {
            return;
        }
        const conentElements = this.contents.map(c => c.element);
        const path = e.composedPath();
        return conentElements.some((el) => {
            return path.includes(el);
        });
    }
    toggleContent(trigger) {
        const isActiveTrigger = this.activeName === trigger.name;
        this.hideActiveContent();
        if (!isActiveTrigger) {
            this.activeName = trigger.name;
            this.contents.forEach(content => {
                if (content.name === trigger.name) {
                    this.updatePosition(trigger, content);
                    this.currentParentElement = this.getScrollParent(trigger.element) || document;
                    this.updatePos = () => this.updatePosition(trigger, content);
                    this.currentParentElement.addEventListener('scroll', this.updatePos);
                }
            });
        }
        this.toggling = true;
        setTimeout(() => this.toggling = false);
    }
    updatePosition(trigger, content) {
        return __awaiter(this, void 0, void 0, function* () {
            content.element.style.position = content.element && content.fixedPosition ? 'fixed' : 'absolute';
            yield content.show();
            // Find element positions
            const triggerPos = trigger.element.getBoundingClientRect();
            const scroll = this.getScroll(content.element);
            const scrollParentPos = this.scrollParentPosition(trigger.element);
            const { offsetParent } = content.element;
            const offsetPos = offsetParent ? offsetParent.getBoundingClientRect() : document.body.getBoundingClientRect();
            const contentPos = content.element.getBoundingClientRect(); // for width & height only
            const contentSpacing = content.spacing ? content.spacing : 0;
            const left = triggerPos.left - offsetPos.left + scroll.left;
            const top = triggerPos.top - offsetPos.top + scroll.top;
            const posOffset = {
                left: 0,
                right: 0 - contentPos.width + triggerPos.width,
                center: 0 - contentPos.width / 2 + triggerPos.width / 2,
                top: 0 - contentSpacing - contentPos.height,
                bottom: triggerPos.height + contentSpacing,
            };
            // Calculate potential left/top positions (for each alignment)
            const pos = {
                left: `${left}px`,
                right: `${left + posOffset.right}px`,
                center: `${left + posOffset.center}px`,
                top: `${top + posOffset.top}px`,
                bottom: `${top + posOffset.bottom}px`,
            };
            // Get positions to check in order, based on settings
            const hPositions = this.hPositions(content);
            const vPositions = this.vPositions(content);
            // Check to see which positions are best, checking in preferred order
            let horiz, visibleWidth;
            const lastWidth = 0;
            let vert, visibleHeight;
            const lastHeight = 0;
            const getVisibleWidth = (p) => {
                const leftEdge = Math.max(scrollParentPos.left, triggerPos.left + posOffset[p]);
                const rightEdge = Math.min(scrollParentPos.right, triggerPos.left + posOffset[p] + contentPos.width);
                return rightEdge - leftEdge;
            };
            const getVisibleHeight = (p) => {
                const topEdge = Math.max(scrollParentPos.top, triggerPos.top + posOffset[p]);
                const bottomEdge = Math.min(scrollParentPos.bottom, triggerPos.top + posOffset[p] + contentPos.height);
                return bottomEdge - topEdge;
            };
            for (let h = 0; h < hPositions.length; h++) {
                visibleWidth = getVisibleWidth(hPositions[h]);
                if (visibleWidth < lastWidth)
                    continue;
                horiz = hPositions[h];
                if (visibleWidth >= contentPos.width)
                    break;
            }
            for (let v = 0; v < vPositions.length; v++) {
                visibleHeight = getVisibleHeight(vPositions[v]);
                if (visibleHeight < lastHeight)
                    continue;
                vert = vPositions[v];
                if (visibleHeight >= contentPos.height)
                    break;
            }
            // Set position based on best fit (horiz & vert)
            content.element.style.left = pos[horiz];
            content.element.style.top = pos[vert];
            content.element.classList.remove(...POSITION_CLASSES);
            // Hide content on vert scroll when trigger is off screen
            const triggerBottom = triggerPos.bottom - scrollParentPos.top;
            const triggerTop = scrollParentPos.bottom - triggerPos.top;
            triggerBottom < 0 || triggerTop < 0 ?
                content.element.classList.add(`${CLASS_PREFIX}--${HIDDEN}`) :
                content.element.classList.remove(`${CLASS_PREFIX}--${HIDDEN}`);
            content.element.classList.add(`${CLASS_PREFIX}--${horiz}`, `${CLASS_PREFIX}--${vert}`);
            const arrow = content.element.querySelectorAll('.se-fe-popover__arrow')[0];
            // Position arrow based on content position
            if (arrow) {
                // Remove any previous styles on arrows
                arrow.removeAttribute('style');
                switch (horiz) {
                    case 'left':
                        arrow.style.left = `${triggerPos.width / 2}px`;
                        break;
                    case 'right':
                        arrow.style.right = `${triggerPos.width / 2 - ARROW_SPACING}px`;
                        break;
                    case 'center':
                        arrow.style.left = `50%`;
                        break;
                }
            }
        });
    }
    hPositions(content) {
        if (content.position.includes(LEFT))
            return [LEFT, RIGHT, CENTER];
        if (content.position.includes(RIGHT))
            return [RIGHT, LEFT, CENTER];
        return [CENTER, LEFT, RIGHT, HIDDEN];
    }
    vPositions(content) {
        return content.position.includes(BOTTOM) ? [BOTTOM, TOP, HIDDEN] : [TOP, BOTTOM, HIDDEN];
    }
    // Method accessor for easy test stubbing
    window() {
        return window;
    }
    // Method accessor for easy test stubbing
    get documentElement() {
        return document.documentElement;
    }
    scrollParentPosition(element) {
        const parent = this.getScrollParent(element);
        if (parent)
            return parent.getBoundingClientRect();
        const width = this.documentElement.clientWidth;
        const height = this.documentElement.clientHeight;
        return {
            width,
            height,
            left: 0,
            top: 0,
            right: width,
            bottom: height,
        };
    }
}
SeFePositionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
SeFePositionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class SeFePositionModule {
}
SeFePositionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFePositionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionModule, imports: [CommonModule] });
SeFePositionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionModule, providers: [SeFePositionService], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFePositionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [],
                    exports: [],
                    providers: [SeFePositionService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SeFePositionModule, SeFePositionService };
